import { Tag } from 'antd';
import { type ColumnFilterItem, type FilterDropdownProps } from 'antd/es/table/interface';
import type { ColumnsType } from 'antd/lib/table';
import { isNil, pick, sortBy } from 'lodash/fp';
import { useMemo, useCallback, type Key, useEffect } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
import { type ApplicationInCustomerListFragment } from '../../../../api/fragments/ApplicationInCustomerList';
import { type CustomerListEndpointContextDataFragment } from '../../../../api/fragments/CustomerListEndpointContextData';
import { type EndpointContextDataFragment } from '../../../../api/fragments/EndpointContextData';
import { type GetCustomerListFiltersQueryHookResult, useListCustomersQuery } from '../../../../api/queries';
import {
    type ApplicationStage,
    type CustomerFilteringRule,
    CustomerSortingField,
    SortingOrder,
} from '../../../../api/types';
import { useCompany, useCompanyContext } from '../../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import { useRouter } from '../../../../components/contexts/RouterContextManager';
import FilterBox from '../../../../components/FilterBox';
import RangeDateFilterBox, { objectToRangeDateValue } from '../../../../components/RangeDateFilterBox';
import useSearchInDropDown from '../../../../components/useSearchInDropDown';
import { useThemeComponents } from '../../../../themes';
import hasPermissions from '../../../../utilities/hasPermissions';
import renderFilterIcon from '../../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../../utilities/renderSearchIcon';
import useFormatDate from '../../../../utilities/useFormatDate';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../../utilities/useSortAndFilterCache';
import { type PaginatedCustomerTable, mapApplicationStage } from '../../../admin/CustomerListPage/CustomerList';
import { useListReducer, defaultInitialState } from '../../../admin/CustomerListPage/useListReducer';
import useSortOrder, { getSortField } from '../../../admin/CustomerListPage/useSortOrder';
import { getStatusColor } from '../../../shared/ApplicationList/helpers';

export type CustomerListCIProps = {
    endpoint: CustomerListEndpointContextDataFragment;
    setModuleListByApplication?: React.Dispatch<React.SetStateAction<string[]>>;
};

type CustomerDataSource = ApplicationInCustomerListFragment & { key: Key };

export const getApplicationReferenceLink = (
    applicationStage: ApplicationStage,
    applicationStageIdentifier: string,
    applicationId: string,
    isClickable: boolean,
    applicationModuleId: string,
    routerEndpoints: EndpointContextDataFragment[]
) => {
    const endpoint = routerEndpoints.find(
        endpoint =>
            endpoint.__typename === 'ApplicationListEndpoint' &&
            endpoint.applicationStage === applicationStage &&
            endpoint.applicationModuleIds.includes(applicationModuleId)
    );
    const getLinkComponent = (link: string) =>
        isClickable ? (
            <Link onClick={e => e.stopPropagation()} to={link}>
                {applicationStageIdentifier}
            </Link>
        ) : (
            applicationStageIdentifier
        );

    if (!isNil(endpoint)) {
        return getLinkComponent(`${endpoint.pathname}/${applicationId}`);
    }

    return applicationStageIdentifier;
};

const CustomerListCI = ({ endpoint, setModuleListByApplication }: CustomerListCIProps) => {
    const { applicationModuleIds } = endpoint;
    const { t } = useTranslation(['applicationList', 'customerList']);
    const { PaginatedTableWithContext } = useThemeComponents();
    const navigate = useNavigate();

    // get company context
    const company = useCompany(true);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Customer);

    // get state from a reducer
    const [state, dispatch] = useListReducer<CustomerSortingField, CustomerFilteringRule>({
        sort: currentCache?.sort || {
            field: CustomerSortingField.CreationDate,
            order: SortingOrder.Desc,
        },
        page: currentCache?.page || defaultInitialState.page,
        pageSize: currentCache?.pageSize || defaultInitialState.pageSize,
        filter: currentCache?.filter || {
            keyword: '',
        },
    });

    const formatDate = useFormatDate();

    // fetch data
    const { page, pageSize, sort, filter } = state;

    const { dealerIds } = useMultipleDealerIds();

    const { data, loading } = useListCustomersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: {
                ...filter,
                applicationModuleIds: filter?.applicationModuleIds ?? applicationModuleIds,
                applicationDealerIds: filter?.applicationDealerIds ?? dealerIds,
            },
        },
    });

    const customerFilterList: GetCustomerListFiltersQueryHookResult['data'] = null;

    const filterListMapper = list =>
        list.map(field => ({
            text: field.displayName,
            value: field.id,
        }));

    const [statusList, companyList, dealerList, moduleList] = useMemo(() => {
        const filterList = customerFilterList?.getCustomerListFilters;
        if (!filterList) {
            return [[], [], [], []];
        }
        const statusList = sortBy(
            'text',
            filterList.applicationStatus.map(status => ({
                text: t(`applicationList:status.${status}`),
                value: status,
            }))
        );
        const companyList = sortBy('text', filterListMapper(filterList.companies));
        const dealerList = sortBy('text', filterListMapper(filterList.dealers));
        const moduleList = sortBy('text', filterListMapper(filterList.modules));

        return [statusList, companyList, dealerList, moduleList];
    }, [customerFilterList, t]);

    useEffect(() => {
        if (setModuleListByApplication) {
            const moduleListIds = moduleList.map(module => module.value);
            setModuleListByApplication(moduleListIds);
        }
    }, [moduleList, setModuleListByApplication]);

    const onRow = useCallback<PaginatedCustomerTable['onRow']>(
        record => ({
            onClick: () => {
                setCache(state);
                navigate(record.versioning.suiteId);
            },
        }),
        [navigate, setCache, state]
    );

    const showRangeFilterDropDown = useCallback(
        (props: FilterDropdownProps) =>
            RangeDateFilterBox({
                filterDropDownProps: props,
                picker: 'date',
            }),
        []
    );

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });
                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filteringRule: {
                            companyIds: !filters.company ? undefined : (filters.company as string[]),
                            applicationIdentifier: !filters.applicationIdentifier
                                ? null
                                : filters.applicationIdentifier[0],
                            assigneeName: !filters.assigneeName ? null : filters.assigneeName[0],
                            fullName: !filters.fullName ? null : filters.fullName[0],
                            applicationModuleIds: !filters.applicationModuleIds ? null : filters.applicationModuleIds,
                            applicationStatuses: !filters.status ? null : filters.status,
                            applicationDealerIds: !filters.dealer ? null : filters.dealer,
                            lastActivity: !filters.lastActivity
                                ? null
                                : {
                                      start: new Date(filters.lastActivity[0] as string),
                                      end: new Date(filters.lastActivity[1] as string),
                                  },
                            creationDate: !filters.creationDate
                                ? null
                                : {
                                      start: new Date(filters.creationDate[0] as string),
                                      end: new Date(filters.creationDate[1] as string),
                                  },
                        },
                    });
                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const renderStatusTag = (status: string, t: TFunction) => {
        const color = getStatusColor(status);

        return (
            <Tag key={status} color={color}>
                {t(`applicationList:status.${status}`)}
            </Tag>
        );
    };

    const router = useRouter();

    // prepare items as a data source
    const dataSource = useMemo(
        () =>
            (data?.list?.items || []).map(item => ({
                ...item,
                key: item.id,
            })),
        [data]
    );
    const total = data?.list?.count || 0;
    const searchBox = useSearchInDropDown();

    const {
        companySort,
        customerNameSort,
        dealerSort,
        appModuleSort,
        assigneeSort,
        appIdSort,
        appStatusSort,
        createDateSort,
        lastActivitySort,
    } = useSortOrder(sort);

    const { timeZonesByModuleId } = useCompanyContext();

    const columns: ColumnsType<CustomerDataSource> = useMemo(() => {
        const cols = [
            {
                key: 'company',
                dataIndex: ['latestApplication', 'module', 'company', 'displayName'],
                title: t('customerList:columns.company'),
            },
            {
                key: 'fullName',
                dataIndex: 'latestFullName',
                onFilterDropdownVisibleChange: searchBox.autoFocus,
                title: t('customerList:columns.fullName'),
            },
            {
                key: 'creationDate',
                dataIndex: ['versioning', 'createdAt'],
                filterDropdown: props => showRangeFilterDropDown(props),
                filterIcon: renderFilterIcon,
                render: (value: string | Date, record: ApplicationInCustomerListFragment) =>
                    !isNil(value)
                        ? formatDate({
                              date: value,
                              timeZone: timeZonesByModuleId[record.moduleId],
                              withOffset: true,
                          })
                        : '',
                title: t('customerList:columns.creationDate'),
                sorter: true,
                sortOrder: createDateSort,
                filteredValue: objectToRangeDateValue(filter.creationDate),
            },
            {
                key: 'dealer',
                dataIndex: ['latestApplication', 'dealer', 'displayName'],
                title: t('customerList:columns.dealer'),
            },
            {
                key: 'assigneeName',
                dataIndex: ['latestApplication', 'assignee', 'displayName'],
                title: t('customerList:columns.assignee'),
            },
            {
                key: 'applicationModuleIds',
                dataIndex: ['latestApplication', 'module', 'displayName'],
                title: t('customerList:columns.module'),
            },
            {
                key: 'applicationIdentifier',
                dataIndex: ['latestApplication'],
                render: (latestApplication: ApplicationInCustomerListFragment) =>
                    getApplicationReferenceLink(
                        mapApplicationStage(latestApplication),
                        latestApplication?.latestStage?.identifier,
                        latestApplication?.versioning?.suiteId,
                        hasPermissions(latestApplication?.permissions, [permissionKind.viewApplications]),
                        latestApplication?.moduleId,
                        router?.endpoints
                    ),
                title: t('customerList:columns.latestApplication'),
            },
            {
                key: 'status',
                dataIndex: ['latestApplication', 'latestStage', 'status'],
                render: value => renderStatusTag(value, t),
                title: t('customerList:columns.latestStatus'),
            },
            {
                key: 'lastActivity',
                dataIndex: ['latestApplication', 'versioning', 'updatedAt'],
                render: (value: string | Date, record: ApplicationInCustomerListFragment) =>
                    !isNil(value)
                        ? formatDate({
                              date: value,
                              timeZone: timeZonesByModuleId[record.moduleId],
                              withOffset: true,
                          })
                        : '',
                title: t('customerList:columns.lastActivity'),
            },
        ];

        return company ? cols.filter(col => col.key !== 'company') : cols;
    }, [
        appIdSort,
        appModuleSort,
        appStatusSort,
        assigneeSort,
        company,
        companyList,
        companySort,
        createDateSort,
        customerNameSort,
        dealerList,
        dealerSort,
        filter.applicationDealerIds,
        filter.applicationIdentifier,
        filter.applicationModuleIds,
        filter.applicationStatuses,
        filter.assigneeName,
        filter.companyIds,
        filter.creationDate,
        filter.fullName,
        filter.lastActivity,
        formatDate,
        lastActivitySort,
        moduleList,
        router?.endpoints,
        searchBox,
        showFilterDropDown,
        showRangeFilterDropDown,
        statusList,
        t,
        timeZonesByModuleId,
    ]);

    return (
        <PaginatedTableWithContext
            columns={columns}
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('customerList:title')}
            total={total}
        />
    );
};

export default CustomerListCI;
